/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
/**
 * QualitativeSignpostCard.js — Sprint 3
 *
 * Displays the latest LLM assessment for a qualitative signpost.
 * - SVG proximity dial: Far → Approaching → Imminent → Triggered
 * - LLM narrative text
 * - Assessed-at timestamp
 * - Trigger condition chips
 */
import React from "react";
import { Chip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ── Proximity Dial ─────────────────────────────────────────────────────────────
const STAGES = ["far", "approaching", "imminent", "triggered"];
const STAGE_COLORS = {
  far: "#4caf50",
  approaching: "#ff9800",
  imminent: "#f44336",
  triggered: "#b71c1c",
};
const STAGE_LABELS = {
  far: "Far",
  approaching: "Approaching",
  imminent: "Imminent",
  triggered: "Triggered",
};

function ProximityDial({ direction = "far" }) {
  const activeIdx = STAGES.indexOf(direction);
  const dialWidth = 240;
  const dialHeight = 36;
  const segW = dialWidth / STAGES.length;

  return (
    <svg width={dialWidth} height={dialHeight + 20} style={{ display: "block" }}>
      {STAGES.map((stage, i) => {
        const isActive = i <= activeIdx;
        return (
          <g key={stage}>
            <rect
              x={i * segW + 1}
              y={0}
              width={segW - 2}
              height={dialHeight}
              rx={i === 0 ? 6 : i === STAGES.length - 1 ? 6 : 0}
              ry={6}
              fill={isActive ? STAGE_COLORS[stage] : "#e0e0e0"}
            />
            <text
              x={i * segW + segW / 2}
              y={dialHeight / 2 + 5}
              textAnchor="middle"
              fontSize={10}
              fill={isActive ? "#fff" : "#888"}
              fontWeight={stage === direction ? "bold" : "normal"}
            >
              {STAGE_LABELS[stage]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────────
export default function QualitativeSignpostCard({ signpost }) {
  const assessment = signpost?.lastAssessment || {};
  const direction = assessment.direction || "far";
  const narrative = assessment.narrative || "";
  const assessedAt = assessment.assessedAt
    ? new Date(assessment.assessedAt).toLocaleString()
    : null;
  const triggers = signpost?.triggers || [];

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={2} mb={1}>
        <ProximityDial direction={direction} />
        <MDBox>
          <MDTypography
            variant="subtitle2"
            sx={{ color: STAGE_COLORS[direction] || "#555", fontWeight: "bold" }}
          >
            {STAGE_LABELS[direction] || direction}
          </MDTypography>
          {assessedAt && (
            <MDTypography variant="caption" color="text.secondary">
              Assessed {assessedAt}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      {narrative ? (
        <MDTypography variant="body2" sx={{ mb: 1, fontStyle: "italic", color: "#444" }}>
          &ldquo;{narrative}&rdquo;
        </MDTypography>
      ) : (
        <MDTypography variant="caption" color="text.secondary">
          LLM assessment not yet run. Will populate on next daily tick once a horizon theme is
          linked.
        </MDTypography>
      )}

      {triggers.length > 0 && (
        <MDBox mt={1.5} display="flex" flexDirection="column" gap={1}>
          {triggers.map((t, i) => {
            const st = t.status || "far";
            const stColor =
              st === "triggered" ? "#c62828" : st === "approaching" ? "#e65100" : "#9e9e9e";
            const ev = Array.isArray(t.evidence) ? t.evidence : [];
            return (
              <MDBox key={i} sx={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: 1, p: 1 }}>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={st.toUpperCase()}
                    sx={{ bgcolor: stColor, color: "#fff", fontWeight: 700, fontSize: "10px" }}
                  />
                  <MDTypography variant="caption" sx={{ fontWeight: 600 }}>
                    {t.triggerType === "residual_threshold"
                      ? "Residual threshold"
                      : "Trigger event"}
                  </MDTypography>
                  {t.triggerType === "residual_threshold" && t.thresholdRating && (
                    <MDTypography variant="caption" sx={{ color: "#666" }}>
                      {`residual `}
                      <strong style={{ color: stColor }}>{t.residualRating || "—"}</strong>
                      {` · alert at ${t.thresholdRating} or worse`}
                    </MDTypography>
                  )}
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  {t.description}
                </MDTypography>
                {t.lastNarrative && (
                  <MDTypography
                    variant="caption"
                    sx={{ display: "block", fontStyle: "italic", color: "#555", mt: 0.25 }}
                  >
                    {t.lastNarrative}
                  </MDTypography>
                )}
                {ev.length > 0 && (
                  <MDBox mt={0.5}>
                    <MDTypography variant="caption" sx={{ fontWeight: 700, color: "#52009b" }}>
                      Evidence
                    </MDTypography>
                    {ev.map((e, ei) => (
                      <MDBox key={ei} sx={{ mt: 0.25 }}>
                        <MDTypography variant="caption" sx={{ display: "block" }}>
                          <span
                            style={{
                              fontWeight: 700,
                              color: e.verdict === "confirmed" ? "#2e7d32" : "#e65100",
                            }}
                          >
                            {String(e.verdict || "").toUpperCase()}
                          </span>{" "}
                          {e.domain}
                          {e.factualRank != null ? ` (rank ${e.factualRank})` : ""}
                          {e.date ? ` · ${e.date}` : ""}
                          {e.url ? (
                            <>
                              {" · "}
                              <a href={e.url} target="_blank" rel="noopener noreferrer">
                                source
                              </a>
                            </>
                          ) : null}
                        </MDTypography>
                        {e.quote && (
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", fontStyle: "italic" }}
                          >
                            &ldquo;{e.quote}&rdquo;
                          </MDTypography>
                        )}
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </MDBox>
  );
}
